/**
 * PR-29A — Contextual CTA engine.
 * Lets engagement modules (poll, prediction, trivia, reward) announce their
 * presence so the floating "Participate" button can render a meaningful label
 * instead of the generic "Participar Ahora".
 */
import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/audit";

export type CtaContextKind = "poll" | "prediction" | "reward" | "trivia" | "none";

const PRIORITY: CtaContextKind[] = ["poll", "prediction", "reward", "trivia"];

const active = new Set<CtaContextKind>();
const listeners = new Set<() => void>();
let last: CtaContextKind = "none";

function resolve(): CtaContextKind {
  for (const k of PRIORITY) if (active.has(k)) return k;
  return "none";
}

function emit() {
  const next = resolve();
  if (next !== last) {
    last = next;
    void trackEvent("contextual_cta_label_updated", { context: next });
  }
  listeners.forEach((fn) => fn());
}

export function setCtaContextActive(kind: CtaContextKind, on: boolean): void {
  if (kind === "none") return;
  const had = active.has(kind);
  if (on && !had) active.add(kind);
  else if (!on && had) active.delete(kind);
  else return;
  emit();
}

export function getActiveCtaContext(): CtaContextKind {
  return resolve();
}

export function useActiveCtaContext(): CtaContextKind {
  const [k, setK] = useState<CtaContextKind>(() => resolve());
  useEffect(() => {
    const fn = () => setK(resolve());
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return k;
}

/** Hook for components to register their active state for the duration of mount. */
export function useRegisterCtaContext(kind: CtaContextKind, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    setCtaContextActive(kind, true);
    return () => setCtaContextActive(kind, false);
  }, [kind, active]);
}

export function ctaLabelKey(kind: CtaContextKind): string {
  switch (kind) {
    case "poll": return "cta_answer_poll";
    case "prediction": return "cta_make_prediction";
    case "reward": return "cta_claim_prize";
    case "trivia": return "cta_play_trivia";
    default: return "cta_earn_points";
  }
}